import { MODULE_ACL, USER_ROLES } from './constant';

export const CAMPAIGNS_ACL_MODULE = MODULE_ACL.CAMPAIGNS;
export const MAX_PACING_RATIO_KEY = 'max_pacing_ratio';
export const VALID_PACING_RATIOS: string[] = Array.from({ length: 9 }, (_, i) => `1:${i + 1}`);
export const DEFAULT_MAX_PACING_RATIO = '1:2';

type ModuleEntry =
  | { view_permission?: boolean; read?: boolean; add?: boolean; max_pacing_ratio?: unknown }
  | null
  | undefined;

export const PACING_RATIOS_TRIGGER_OPS = ['view_permission', 'read', 'add'] as const;
type ModuleOps = Record<string, ModuleEntry>;

function parseOps(raw: unknown): ModuleOps {
  if (!raw) return {};
  if (typeof raw === 'object') return raw as ModuleOps;
  try {
    return (JSON.parse(String(raw)) as ModuleOps) || {};
  } catch {
    return {};
  }
}

export function pacingRatioRank(ratio: unknown): number {
  const r = String(ratio ?? '').trim();
  return VALID_PACING_RATIOS.includes(r) ? Number(r.slice(2)) : 0;
}

export function effectiveMaxPacingRatio(entry: ModuleEntry): string {
  const stored = String(entry?.max_pacing_ratio ?? '').trim();
  return pacingRatioRank(stored) ? stored : DEFAULT_MAX_PACING_RATIO;
}

/** Ratios the holder of these module operations may use on create, and may grant as a max: 1:1 up to their max. */
export function getAllowedPacingRatios(moduleOperations: unknown, isSuperAdmin: boolean): string[] {
  if (isSuperAdmin) return [...VALID_PACING_RATIOS];
  const max = effectiveMaxPacingRatio(parseOps(moduleOperations)[CAMPAIGNS_ACL_MODULE]);
  return VALID_PACING_RATIOS.slice(0, pacingRatioRank(max));
}

export function getUserAllowedPacingRatios(
  user: { role?: string; acl_settings?: { module_operations?: unknown } } | null | undefined,
): string[] {
  return getAllowedPacingRatios(
    user?.acl_settings?.module_operations,
    user?.role === USER_ROLES.SUPERADMIN,
  );
}

export function isPacingRatiosRelevant(entry: ModuleEntry): boolean {
  return PACING_RATIOS_TRIGGER_OPS.some((op) => entry?.[op] === true);
}

export function resolveMaxPacingRatioForSave(stored: unknown, parentGrantable: string[]): string {
  const s = String(stored ?? '').trim();
  if (pacingRatioRank(s) && parentGrantable.includes(s)) return s;
  return parentGrantable.includes(DEFAULT_MAX_PACING_RATIO)
    ? DEFAULT_MAX_PACING_RATIO
    : parentGrantable[parentGrantable.length - 1] ?? VALID_PACING_RATIOS[0]!;
}

export function formatPacingRatiosLabel(entry: ModuleEntry): string {
  if (!isPacingRatiosRelevant(entry)) return '—';
  return `Pacing up to ${effectiveMaxPacingRatio(entry)}`;
}
